import { useState, useEffect } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

export function useStrReport(epicKey) {
  const [epicData, setEpicData] = useState(null);
  const [scoreData, setScoreData] = useState(null);
  const [scoreError, setScoreError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!epicKey) {
      setEpicData(null);
      setScoreData(null);
      setScoreError(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setEpicData(null);
    setScoreData(null);
    setScoreError(null);

    Promise.allSettled([
      api.get(`${QUALITY_SERVICE_API_URL}/hub/software-test-report/${epicKey}`),
      api.get(`${QUALITY_SERVICE_API_URL}/hub/software-test-report/${epicKey}/score`)
    ])
      .then((results) => {
        if (cancelled) return;

        const epicResult = results[0];
        const strResult = results[1];

        if (epicResult.status === 'fulfilled') {
          setEpicData(epicResult.value.data);
        } else {
          console.error('Error fetching epic data:', epicResult.reason);
          setError(epicResult.reason?.message || 'Failed to load epic data');
          setLoading(false);
          return;
        }

        // A missing score (404) is expected until the STR agent has run for this epic;
        // any other failure is surfaced so an outage isn't shown as "not generated yet"
        if (strResult.status === 'fulfilled') {
          setScoreData(strResult.value.data);
        } else if (strResult.reason?.response?.status === 404) {
          console.warn('Score not available yet:', strResult.reason?.message);
        } else {
          console.error('Error fetching readiness score:', strResult.reason);
          setScoreError(strResult.reason?.message || 'Failed to load readiness score');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching STR report:', err);
        setError(err.message || 'Failed to load report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [epicKey, retryCount]);

  const retry = () => {
    setRetryCount((prev) => prev + 1);
  };

  return { epicData, scoreData, scoreError, loading, error, retry };
}
